// Considered: RelationshipSheetMolecule with an `override` mode — rejected. The two sheets
// share a shell, not a form: every control here is anchored to an ancestor's value, can only
// move one way, and carries its own inherited read-back and revert. Folding that into the
// authoring sheet would put an `if (override)` on every field and give the composer one
// component with two incompatible contracts. The shell IS reused — same scrim-in-place shape
// as `RelationshipSheetMolecule` / `AspectAttachDialogOrganism`, for the same reason
// (`CustomDialog` portals to `document.body`, which puts every datum outside the composer's
// `relationships` container and makes the parity walker judge the sheet as uncontained).
//
// Extra conditions: the platform's one filter tree (`UnifiedFilterTreeMolecule`, narrow, no
// groups). They persist as a flat AND list (`extraFilters` → `target_filter.propertyFilters`),
// so the tree's root operator is fixed to `and` and converted at this boundary.
//
// Scope note: the ancestor's "any sphere" branch delegates to `ApplicabilityEditorMolecule`
// — the platform's `<SphereScopeEditor>` — because there is nothing to narrow, so the subtype
// authors a rule from scratch and it must read the way every other target rule reads.
import React, { useEffect, useMemo, useState } from 'react';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import { useTranslation } from 'localization';
import type { SchemaRelationship, OrgAvailableTypeSchema } from 'types/schema';
import type { FilterExpression, FilterGroup, UnifiedFilters } from 'types/filters';
import InputAtom from '../../atoms/Input/InputAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import UnifiedFilterTreeMolecule from '../UnifiedFilterTree/UnifiedFilterTreeMolecule';
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import { nextFilterId, withStableIds } from '../../../utils/ids';
import { readRelationshipScope } from './relationshipScope';
import ApplicabilityEditorMolecule, {
  type ApplicabilityEditorInitialState,
  type ApplicabilityEditorParity,
  type ApplicabilityMode,
  useApplicabilityConditionCatalog,
} from '../ApplicabilityEditor/ApplicabilityEditorMolecule';
import type { PropertyOption } from '../PropertyFilter/PropertyFilterMolecule';
import type { SchemaSource } from '../SphereTypeSelect';
import { deriveApplicability, buildApplicability } from '../../../utils/contextFlowSerialization';
import { relCardAllowed, relEffective, type RelationshipOverridePatch } from './relationshipOverride';
import { relationshipScopeCopy } from './relationshipScopeCopy';
import './RelationshipOverrideSheetMolecule.scss';

const KEY = 'studio.typeEditor.relationships';
const OV = `${KEY}.override`;

/**
 * The scope editor's contract ids at its SECOND call site — the one this sheet mounts when
 * the ancestor allows any sphere, so there is nothing to narrow and the subtype authors a
 * target rule from scratch.
 *
 * The decomposition walks `<SphereScopeEditor>` once per host, so every reading of the editor
 * appears twice under different ids: these are the `relOvSheet=yes+yes & baseScope.mode=any`
 * series, the same shape as `RelationshipSheetMolecule`'s `SCOPE_PARITY` with each id at its
 * second occurrence. The two maps stay separate rather than being generated from an offset —
 * an id is a contract fact, and deriving one from arithmetic would silently re-point every
 * datum the next regeneration renumbers.
 */
const OVERRIDE_SCOPE_PARITY: ApplicabilityEditorParity = {
  modeOption: 'rels.opt-label#9',
  familyHint: 'rels.familyhint#2',
  unmapped: 'rels.unmapped-join#2',
  legacyMode: 'rels.custom#2',
  done: 'rels.done#2',
  typeSelect: {
    trigger: 'rels.change#2',
    chipRemove: 'rels.button#39',
    clear: 'rels.clear#2',
    search: 'rels.input#15',
    searchClear: 'rels.button#40',
    familyOption: 'rels.types#2',
    familyGroup: 'rels.button#41',
    typeOption: 'rels.button#42',
  },
  /** The built-in condition catalog's scope banner, at this call site. */
  catalog: {
    searchingCount: 'rels.ef-org-type-count#4',
    scopedCount: 'rels.type#4',
    coreScope: 'rels.core#4',
  },
};

/** Contract order (`type-relationship.jsx:21-26`), not the type union's order. */
const REL_CARDS: SchemaRelationship['cardinality'][] = [
  'one_to_one',
  'many_to_one',
  'one_to_many',
  'many_to_many',
];


export interface RelationshipOverrideSheetMoleculeProps {
  visible: boolean;
  /** The ancestor's declaration. Never mutated — the sheet only ever emits a patch. */
  base: SchemaRelationship;
  /** The ancestor's display name — every sentence in this sheet names it. */
  from: string;
  /** The adjusting type's display name. */
  typeName: string;
  /**
   * Renders as the rail's current selection body (no scrim, no `role="dialog"`/`aria-modal`, no
   * own focus trap — the host, `InspectorRailOrganism`, already owns Escape and containment
   * non-modally), the same convention `RelationshipSheetMolecule.railMode` established in
   * EAINT-933 Phase 3.2. Default `false` keeps the standalone-modal shape for any host that has
   * not moved to the rail.
   */
  railMode?: boolean;
  /** The adjustments this type has already saved, if any. */
  override?: RelationshipOverridePatch | null;
  onHide: () => void;
  /** `null` = the type adjusted nothing, so the row goes back to reading as the ancestor's. */
  onSave: (patch: RelationshipOverridePatch | null) => void;
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  availableProperties?: PropertyOption[];
  sources?: Record<string, SchemaSource>;
  approachSources?: Record<string, string[]>;
  /** Seeds the any-sphere branch's scope editor, so a story can declare one of its readings. */
  initialScopeState?: ApplicabilityEditorInitialState;
  /**
   * Overrides the mode `deriveApplicability` reads out of the saved patch. The mode strip is
   * editor state, not part of the rule: switching it from Custom to a type list keeps the
   * conditions in state while `buildApplicability` writes only the list — which is what the
   * *Carried over from the old definition* note states. No saved patch can express that
   * pairing, so a story or a test that needs the note's second line declares the mode here.
   */
  initialScopeMode?: ApplicabilityMode;
  className?: string;
  /**
   * Fires whenever "has the author changed something worth losing" changes, compared against
   * the `override` patch this sheet loaded (EAINT-933 Phase 3.3, extended from
   * `RelationshipSheetMolecule.onDirtyChange` to the override sheet). A host that closes the
   * rail selection without checking this discards whatever was adjusted with no warning.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * One inherited field: what it is called, whether this type moved it, what the ancestor
 * declared, the control, and the rule that governs which way it may move.
 *
 * The five readings sit in one container so the parity walker places them exactly where the
 * decomposition does (`<RelInheritRow>`).
 */
const InheritRow: React.FC<{
  label: string;
  inherited?: string | null;
  overridden: boolean;
  onRevert?: () => void;
  note?: string;
  datums: { label: string; revert: string; inherited: string; note: string };
  testId: string;
  children: React.ReactNode;
}> = ({ label, inherited, overridden, onRevert, note, datums, testId, children }) => {
  const { t } = useTranslation();
  return (
    <div className="rel-override__row" data-testid={testId}>
      <div className="rel-override__row-head">
        <span className="rel-override__row-label" data-datum={datums.label} data-kind="label">
          {label}
        </span>
        {/* The provenance answer sits at the far edge, opposite the field it is about. */}
        <span className="rel-override__row-aside">
          {overridden && onRevert ? (
            <ButtonAtom
              variant="text"
              size="small"
              icon="pi pi-undo"
              label={t(`${OV}.revert`)}
              onClick={onRevert}
              data-datum={datums.revert}
              data-kind="button"
              data-testid={`${testId}-revert`}
            />
          ) : (
            <span className="rel-override__row-tag">{t(`${OV}.inheritedTag`)}</span>
          )}
        </span>
      </div>
      {inherited != null && (
        <span className="rel-override__row-inherited">
          {t(`${OV}.declaredAsBefore`)}{' '}
          <b data-datum={datums.inherited} data-kind="strong">
            {inherited}
          </b>
        </span>
      )}
      {children}
      {note && (
        <span className="rel-override__row-note" data-datum={datums.note} data-kind="hint">
          {note}
        </span>
      )}
    </div>
  );
};

const RelationshipOverrideSheetMolecule: React.FC<RelationshipOverrideSheetMoleculeProps> = ({
  visible,
  base,
  from,
  typeName,
  railMode = false,
  override,
  onHide,
  onSave,
  familyTypeSchemas = [],
  availableProperties = [],
  sources,
  approachSources,
  initialScopeState,
  initialScopeMode,
  className,
  onDirtyChange,
}) => {
  const { t } = useTranslation();
  const titleId = 'relationship-override-title';

  const [ov, setOv] = useState<RelationshipOverridePatch>(() => ({ extraFilters: [], ...(override ?? {}) }));
  const set = (patch: RelationshipOverridePatch) => setOv((prev) => ({ ...prev, ...patch }));

  /** What the sheet loaded for this open — dirtiness is a comparison against THIS, the same
   * seam `RelationshipSheetMolecule.initialRef` established. Reset only when a fresh override
   * target opens (the composer remounts the portal's contents, not this component, so a
   * `base.system_name`/`override` dependency — not `visible` — is what actually changes here). */
  const initialOvRef = React.useRef(ov);
  useEffect(() => {
    initialOvRef.current = { extraFilters: [], ...(override ?? {}) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.system_name]);

  const isDirty = JSON.stringify(ov) !== JSON.stringify(initialOvRef.current);
  useEffect(() => {
    onDirtyChange?.(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  /*
    The ancestor's rule read into the shared model. Families and types are ONE list here —
    both are ids the subtype may drop — because that is what narrowing acts on; the
    families-vs-types distinction only matters to the picker, which this branch never shows.
  */
  const baseScope = useMemo(() => deriveApplicability(base.target_filter), [base.target_filter]);
  const baseIds = useMemo(
    () => [...(baseScope.applicabilityFamilies ?? []), ...(baseScope.applicabilityTypes ?? [])],
    [baseScope]
  );
  const allowsAnySphere = baseScope.applicabilityMode !== 'specific' || baseIds.length === 0;

  /** `system_name` → display name, so a dropped target reads as a name and not as a key. */
  const catalog = useMemo(() => {
    const map = new Map<string, string>();
    for (const schema of familyTypeSchemas) map.set(schema.system_name, schema.name);
    return map;
  }, [familyTypeSchemas]);
  /** Ids the ancestor allows that this workspace's catalog cannot resolve — stated, not dropped. */
  const unmapped = baseIds.filter((id) => !catalog.has(id));
  const mapped = baseIds.filter((id) => catalog.has(id));

  const eff = relEffective(base, ov, baseIds);
  const kept = eff.kept;
  const extras = ov.extraFilters ?? [];

  /*
    The mode strip is editor state. `buildApplicability` writes a type list OR conditions, so
    a rule carrying conditions always reads back as `custom` — which means the mode a person
    switched to is not always recoverable from what was saved. Holding it beside the patch is
    what lets the carried-over note say the conditions are no longer being applied.
  */
  const [modeOverride, setModeOverride] = useState<ApplicabilityMode | undefined>(initialScopeMode);
  const scopeState = useMemo(
    () => readRelationshipScope(ov.scope ?? undefined, modeOverride),
    [ov.scope, modeOverride]
  );

  const [extrasRootId] = useState(nextFilterId);
  /** The extras catalog is scoped to what the inherited rule still reaches on this type. */
  const keptLabels = kept.map((id) => catalog.get(id) ?? id);
  const renderExtrasCatalog = useApplicabilityConditionCatalog({
    availableProperties,
    scopeTypeLabels: keptLabels,
    searchTotalTypeCount: familyTypeSchemas.length,
  });

  const dialogRef = React.useRef<HTMLDivElement>(null);
  // Rail mode: an expanded selection inside the rail, not a modal — the rail's own
  // `useDialogFocus` (`trapFocus: false`) already manages focus and Escape at its container,
  // same as `RelationshipSheetMolecule`'s `side > relationship` mount.
  useDialogFocus(visible, dialogRef, railMode ? { onDismiss: onHide, trapFocus: false, manageFocus: false } : { onDismiss: onHide });

  if (!visible) return null;

  const setScope = (next: Partial<typeof scopeState>) => {
    const merged = { ...scopeState, ...next };
    if (next.mode) setModeOverride(next.mode);
    set({
      scope: (buildApplicability({
        applicabilityMode: merged.mode,
        applicabilityFamilies: merged.families,
        applicabilityTypes: merged.types,
        applicabilityFilter: merged.filter,
      }) ?? {}) as UnifiedFilters,
    });
  };

  /* Narrowing to nothing isn't narrowing — it would leave the picker with nothing to offer. */
  const toggleKeep = (id: string) => {
    const next = kept.includes(id) ? kept.filter((x) => x !== id) : [...kept, id];
    if (!next.length) return;
    set({ keep: next.length === baseIds.length ? null : next });
  };

  /** `extraFilters` read as the tree's root: its leaves in order, joined by the AND they are stored as. */
  const extrasRoot: FilterGroup = { id: extrasRootId, operator: 'and', expressions: withStableIds(extras) };
  /** …and the tree's root written back: its property leaves in order (no groups are offered). */
  const setExtrasFromRoot = (next: FilterGroup) =>
    set({ extraFilters: next.expressions.filter(isPropertyLeaf) as FilterExpression[] });

  const content = (
      <div
        className={['rel-override', railMode ? className : null].filter(Boolean).join(' ')}
        data-datum="rels.dialog#4"
        data-kind="dialog"
        data-testid="relationship-override"
        ref={dialogRef}
        role={railMode ? undefined : 'dialog'}
        aria-modal={railMode ? undefined : 'true'}
        aria-labelledby={titleId}
        onClick={railMode ? undefined : (event) => event.stopPropagation()}
      >
        <div className="rel-override__head">
          <b id={titleId} data-datum="rels.adjust" data-kind="strong">
            {t(`${OV}.title`, { name: base.name })}
          </b>
          <span className="rel-override__crumb" data-datum="rels.declared-by#2" data-kind="breadcrumb">
            {t(`${OV}.crumb`, { from, typeName })}
          </span>
          <ButtonAtom
            variant="text"
            size="small"
            label={t(`${KEY}.cancel`)}
            onClick={onHide}
            data-datum="rels.cancel#2"
            data-kind="button"
            data-testid="relationship-override-cancel"
          />
          <ButtonAtom
            variant="primary"
            size="small"
            label={eff.adjusted.length ? t(`${OV}.saveAdjustments`) : t(`${KEY}.save`)}
            onClick={() => onSave(eff.adjusted.length ? ov : null)}
            data-datum="rels.save-adjustments"
            data-kind="button"
            data-testid="relationship-override-save"
          />
        </div>

        <div className="rel-override__body">
          <p className="rel-override__intro">
            {t(`${OV}.introBefore`)}{' '}
            <b data-datum="rels.from" data-kind="strong">
              {from}
            </b>
            {t(`${OV}.introAfter`, { from, typeName })}
          </p>

          <h3 className="rel-override__section" data-datum="rels.what-it-s-called-on" data-kind="heading">
            {t(`${OV}.sectionCalled`, { typeName })}
          </h3>
          <InheritRow
            label={t(`${KEY}.nameLabel`)}
            inherited={base.name}
            overridden={!!(ov.name && ov.name !== base.name)}
            onRevert={() => set({ name: null })}
            datums={{
              label: 'rels.label#14',
              revert: 'rels.revert-to-inherited',
              inherited: 'rels.inherited',
              note: 'rels.note',
            }}
            testId="relationship-override-name"
          >
            <InputAtom
              value={ov.name ?? base.name ?? ''}
              onChange={(e) => set({ name: e.target.value })}
              aria-label={t(`${KEY}.nameLabel`)}
              data-datum="rels.input#12"
              data-kind="input"
              data-testid="relationship-override-name-input"
            />
          </InheritRow>
          <InheritRow
            label={t(`${KEY}.descriptionLabel`)}
            inherited={base.description || t(`${OV}.notSetDash`)}
            overridden={!!(ov.description && ov.description !== base.description)}
            onRevert={() => set({ description: null })}
            datums={{
              label: 'rels.label#15',
              revert: 'rels.revert-to-inherited#2',
              inherited: 'rels.inherited#2',
              note: 'rels.note#2',
            }}
            testId="relationship-override-description"
          >
            <InputAtom
              value={ov.description ?? base.description ?? ''}
              onChange={(e) => set({ description: e.target.value })}
              aria-label={t(`${KEY}.descriptionLabel`)}
              data-datum="rels.input#13"
              data-kind="input"
              data-testid="relationship-override-description-input"
            />
          </InheritRow>
          <InheritRow
            label={t(`${KEY}.reverseLabel`)}
            inherited={base.reverse_label || t(`${OV}.notSet`)}
            overridden={!!(ov.reverse_label && ov.reverse_label !== base.reverse_label)}
            onRevert={() => set({ reverse_label: null })}
            datums={{
              label: 'rels.label#16',
              revert: 'rels.revert-to-inherited#3',
              inherited: 'rels.inherited#3',
              note: 'rels.note#3',
            }}
            testId="relationship-override-reverse"
          >
            <InputAtom
              value={ov.reverse_label ?? base.reverse_label ?? ''}
              onChange={(e) => set({ reverse_label: e.target.value })}
              placeholder={base.reverse_label || t(`${KEY}.reversePlaceholder`)}
              aria-label={t(`${KEY}.reverseLabel`)}
              data-datum="rels.input#14"
              data-kind="input"
              data-testid="relationship-override-reverse-input"
            />
          </InheritRow>

          {/* Not the declare sheet's head: an inherited link already points somewhere, and
              this section only ever narrows that — so the contract words it as what it can
              point at rather than what may sit on the other end. */}
          <h3 className="rel-override__section" data-datum="rels.what-it-can-point-at" data-kind="heading">
            {t(`${OV}.sectionOtherEnd`)}
          </h3>
          {allowsAnySphere ? (
            <>
              <p className="rel-override__lead">{t(`${OV}.anySphereLead`, { from, typeName })}</p>
              <ApplicabilityEditorMolecule
                allowFilterGroups={false}
                layout="narrow"
                mode={scopeState.mode}
                families={scopeState.families}
                types={scopeState.types}
                filter={scopeState.filter}
                onModeChange={(mode) => setScope({ mode })}
                onFamiliesChange={(families) => setScope({ families })}
                onTypesChange={(types) => setScope({ types })}
                onFilterChange={(filter) => setScope({ filter })}
                availableProperties={availableProperties}
                familyTypeSchemas={familyTypeSchemas}
                sources={sources}
                approachSources={approachSources}
                parity={OVERRIDE_SCOPE_PARITY}
                initialState={initialScopeState}
                copy={relationshipScopeCopy(t)}
                lead={t(`${OV}.anySphereEditorLead`, { typeName })}
                anyNote={t(`${OV}.anySphereUnchanged`, { from })}
              />
              <span className="rel-override__footnote" data-datum="rels.footnote#2" data-kind="hint">
                {t(`${OV}.scopeFootnote`)}
              </span>
            </>
          ) : (
            <>
              <p className="rel-override__lead">
                {t(`${OV}.narrowLead`, {
                  from,
                  typeName,
                  what: t(`${OV}.${baseScope.applicabilityFamilies?.length ? 'theseFamilies' : 'theseTypes'}`),
                })}
              </p>
              <div className="rel-override__keeps" role="group" aria-label={t(`${KEY}.sectionOtherEnd`)}>
                {mapped.map((id, index) => (
                  <button
                    key={id}
                    type="button"
                    role="checkbox"
                    aria-checked={kept.includes(id)}
                    className={`rel-override__keep${kept.includes(id) ? ' rel-override__keep--on' : ''}`}
                    data-container="each(baseIds)"
                    onClick={() => toggleKeep(id)}
                    data-testid={`relationship-override-keep-${id}`}
                    {...(index === 0 ? { 'data-datum': 'rels.meta-name', 'data-kind': 'button' } : {})}
                  >
                    <i className={`pi ${kept.includes(id) ? 'pi-check' : 'pi-times'}`} aria-hidden="true" />
                    {catalog.get(id)}
                  </button>
                ))}
              </div>
              {unmapped.length > 0 && (
                <span className="rel-override__hint" data-datum="rels.also-allows" data-kind="hint">
                  {t(`${OV}.unmapped`, { from, names: unmapped.join(', ') })}
                </span>
              )}
              <span className="rel-override__row-label" data-datum="rels.extra-conditions-on" data-kind="label">
                {t(`${OV}.extraConditionsOn`, { typeName })}
              </span>
              <span className="rel-override__lead">{t(`${OV}.extraConditionsNote`)}</span>
              <UnifiedFilterTreeMolecule
                value={extrasRoot}
                onChange={setExtrasFromRoot}
                availableProperties={availableProperties}
                renderCatalog={renderExtrasCatalog}
                layout="narrow"
                allowGroups={false}
                fixedOperator="and"
                readsAs={false}
                emptyNote={t(`${KEY}.scopeConditionsEmpty`)}
                defaultOpen
                datumScope="relationship-override-extras"
                container="ui-override-extras"
                className="rel-override__extras"
              />
            </>
          )}

          <h3 className="rel-override__section" data-datum="rels.how-it-behaves#2" data-kind="heading">
            {t(`${KEY}.sectionHowItBehaves`)}
          </h3>
          <InheritRow
            label={t(`${KEY}.kindLabel`)}
            inherited={t(`${KEY}.kind.${base.relationship_type}`)}
            overridden={false}
            note={t(`${OV}.kindNote`, { from })}
            datums={{
              label: 'rels.label#29',
              revert: 'rels.revert-to-inherited#4',
              inherited: 'rels.inherited#4',
              note: 'rels.note#4',
            }}
            testId="relationship-override-kind"
          >
            {/* Locked, and stating why in its note rather than hiding the reason (ux-craft I2). */}
            <div className="rel-override__locked" aria-disabled="true">
              <i className="pi pi-lock" aria-hidden="true" />
              <b data-datum="rels.rel-kinds-find-k-k-value-base-kind-label" data-kind="strong">
                {t(`${KEY}.kind.${base.relationship_type}`)}
              </b>
            </div>
          </InheritRow>
          <InheritRow
            label={t(`${KEY}.howManyLabel`)}
            inherited={t(`${KEY}.cardinality.${base.cardinality}`)}
            overridden={!!(ov.cardinality && ov.cardinality !== base.cardinality)}
            onRevert={() => set({ cardinality: null })}
            note={t(`${OV}.cardinalityNote`)}
            datums={{
              label: 'rels.label#30',
              revert: 'rels.revert-to-inherited#5',
              inherited: 'rels.inherited#5',
              note: 'rels.note#5',
            }}
            testId="relationship-override-cardinality"
          >
            <div className="rel-override__cards" role="radiogroup" aria-label={t(`${KEY}.howManyLabel`)}>
              {REL_CARDS.map((value, index) => {
                const allowed = relCardAllowed(base.cardinality, value);
                const on = eff.cardinality === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={!allowed}
                    className={`rel-override__card${on ? ' rel-override__card--on' : ''}`}
                    data-container="each(cards)"
                    onClick={() => set({ cardinality: value === base.cardinality ? null : value })}
                    data-testid={`relationship-override-cardinality-${value}`}
                    {...(index === 0 ? { 'data-datum': 'rels.button#89', 'data-kind': 'button' } : {})}
                  >
                    <span className="rel-override__card-title">
                      {t(`${KEY}.cardinality.${value}`)}
                      {value === base.cardinality && (
                        <i className="rel-override__card-inherited">{t(`${OV}.inheritedSuffix`)}</i>
                      )}
                    </span>
                    {/* A disabled choice states why rather than only dimming (ux-craft I2). */}
                    <span className="rel-override__card-note">
                      {allowed ? t(`${KEY}.cardinalityNote.${value}`) : t(`${OV}.looserThan`, { from })}
                    </span>
                  </button>
                );
              })}
            </div>
          </InheritRow>
          <InheritRow
            label={t('studio.typeEditor.row.required')}
            inherited={base.required ? t(`${OV}.required`) : t(`${OV}.optional`)}
            overridden={!!(ov.required && !base.required)}
            onRevert={() => set({ required: false })}
            note={base.required ? t(`${OV}.requiredAlready`, { from }) : t(`${OV}.requiredHereOnly`)}
            datums={{
              label: 'rels.label#31',
              revert: 'rels.revert-to-inherited#6',
              inherited: 'rels.inherited#6',
              note: 'rels.note#6',
            }}
            testId="relationship-override-required"
          >
            <button
              type="button"
              role="switch"
              aria-checked={eff.required}
              disabled={base.required}
              className={`rel-override__required${eff.required ? ' rel-override__required--on' : ''}`}
              onClick={() => set({ required: !ov.required })}
              data-datum="rels.a-sphere-of-this-type-can-t-be-saved-wit"
              data-kind="button"
              data-testid="relationship-override-required-toggle"
            >
              <i className={`pi ${eff.required ? 'pi-check-square' : 'pi-stop'}`} aria-hidden="true" />
              <span>{t(`${OV}.requiredToggle`)}</span>
            </button>
          </InheritRow>

          <p
            className={`rel-override__summary${eff.adjusted.length ? ' rel-override__summary--adjusted' : ''}`}
            data-testid="relationship-override-summary"
          >
            {eff.adjusted.length ? (
              <>
                <b data-datum="rels.eff-adjusted-join" data-kind="strong">
                  {eff.adjusted.map((a) => t(`${OV}.adjustment.${a}`)).join(', ')}
                </b>
                {t(`${OV}.adjustedSummary`, { typeName, from })}
              </>
            ) : (
              t(`${OV}.nothingAdjusted`, { typeName, from })
            )}
          </p>
        </div>
      </div>
  );

  if (railMode) return content;

  return (
    /*
      The scrim closes on click and carries no keyboard role of its own: Cancel is the
      keyboard path, and a `role="button"` here would put an unlabelled control in the tab
      order in front of the sheet's own. Same reasoning as `RelationshipSheetMolecule`.
    */
    <div
      className={['rel-override__scrim', className].filter(Boolean).join(' ')}
      data-datum="rels.dialog#3"
      data-kind="dialog"
      data-testid="relationship-override-scrim"
      role="presentation"
      onClick={onHide}
    >
      {content}
    </div>
  );
};

RelationshipOverrideSheetMolecule.displayName = 'RelationshipOverrideSheetMolecule';

export default RelationshipOverrideSheetMolecule;
